import { Button } from '@/components/ui/button';
import { Link } from 'react-router';
import {
    CalendarDays,
    ChefHat,
    UtensilsCrossed,
    BookOpen,
    ArrowRight,
    Clock,
    Star,
    CheckCircle2,
    Circle,
    ClipboardList,
    LineChart,
} from 'lucide-react';
import { useMyProgram } from '@/services/programs';
import {
    useMyEntriesByDate,
    useMyDailyPointsSummary,
    useMyDailyMeals,
} from '@/hooks/useJournalApi';
import { WeeklyReportPrompt } from '@/components/progress/WeeklyReportPrompt';
import { useAuthStore } from '@/store/useAuthStore';
import { todayLocalDateStr } from '@/lib/date';
import { RecipeImage } from '@/components/RecipeImage';
import { useMyPlan } from '@/services/planShopping';
import { ShopForPlanCard } from '@/components/ShopForPlanCard';
import { formatLongDate } from '@/lib/planShopping';
import { Feature } from '@/types/auth';

const Dashboard = () => {
    const today = todayLocalDateStr();
    const { user, hasFeature } = useAuthStore();
    const firstName = user?.firstName || 'there';

    // Get today's menu from assigned program
    const { data: assignment, isLoading: assignmentLoading } = useMyProgram();
    // The whole plan, including one that has not started yet (#330).
    const { data: plan } = useMyPlan();
    const planUpcoming = plan?.phase === 'UPCOMING';

    // Real journal data
    const { data: todayEntries = [] } = useMyEntriesByDate(today);
    const { data: dailyPointsSummary } = useMyDailyPointsSummary(today);
    const { data: dailyMeals = [] } = useMyDailyMeals(today);

    // Calculate journal status
    const todayJournalEntry = todayEntries.find((entry) => entry.template?.type === 'DAILY');
    const hasJournalEntry = !!todayJournalEntry;
    // Today's points are the day's logged meals. The journal entry's
    // pointsEarned is derived from those same meals (#223), so adding it on
    // top counted every point twice once the day had an entry (#371).
    const totalWellnessPoints = dailyPointsSummary?.totalPoints || 0;
    const isJournalComplete = todayJournalEntry?.isCompleted || false;
    const mealsLogged = dailyMeals.length;

    // Calculate which day of the program we're on
    const calculateDayNumber = () => {
        if (!assignment?.startDate) return 0;
        const start = new Date(assignment.startDate);
        const now = new Date();
        start.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        const diffTime = now.getTime() - start.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays + 1; // Day 1 is the start date
    };

    const currentDayNumber = calculateDayNumber();
    const todayProgramDay = assignment?.program?.days?.find(
        (d) => d.dayNumber === currentDayNumber,
    );
    const todayMeals = todayProgramDay?.meals || [];

    // Greeting based on time of day
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    return (
        <div className="min-h-screen bg-brand-page">
            {/* Hero Section */}
            <div className="bg-brand-header text-brand-header-ink">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <h1 className="text-3xl font-bold mb-2">
                        {getGreeting()}, {firstName}!
                    </h1>
                    <p className="text-brand-header-ink/80">
                        {new Date().toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                        })}
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Above the grid and above the fold, on purpose. This is the
                    one thing on the page with a deadline attached (#244). */}
                <WeeklyReportPrompt />

                {plan && hasFeature(Feature.SHOPPING_LISTS) && <ShopForPlanCard plan={plan} />}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content - Left 2 columns */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Today's Menu Section */}
                        <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                            <div className="p-6 border-b border-border">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                        <CalendarDays className="w-5 h-5 text-brand-text-blue" />
                                        Today's Menu
                                    </h2>
                                    <Link to="/my-menu">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-brand-text-blue hover:bg-brand-header-wash"
                                        >
                                            View Full Week
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </Button>
                                    </Link>
                                </div>
                                {assignment?.program && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        From: {assignment.program.name}
                                    </p>
                                )}
                            </div>

                            <div className="p-6">
                                {assignmentLoading ? (
                                    <div className="space-y-4">
                                        {[1, 2, 3].map((i) => (
                                            <div
                                                key={i}
                                                className="animate-pulse flex items-center gap-4"
                                            >
                                                <div className="w-16 h-16 bg-muted rounded-xl" />
                                                <div className="flex-1">
                                                    <div className="h-4 bg-muted rounded w-24 mb-2" />
                                                    <div className="h-5 bg-muted rounded w-48" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : todayMeals.length > 0 ? (
                                    <div className="space-y-4">
                                        {todayMeals.map(
                                            (meal: {
                                                id: string;
                                                mealType: string;
                                                recipe: {
                                                    id: string;
                                                    name: string;
                                                    imageUrl?: string;
                                                    prepTime?: number;
                                                    cookTime?: number;
                                                };
                                            }) => (
                                                <Link
                                                    key={meal.id}
                                                    to={`/recipes/${meal.recipe.id}`}
                                                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-brand-header-wash transition-colors group"
                                                >
                                                    {/* Meal Image */}
                                                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-brand-header-wash shrink-0">
                                                        {meal.recipe.imageUrl ? (
                                                            <RecipeImage
                                                                src={meal.recipe.imageUrl}
                                                                alt={meal.recipe.name}
                                                                variant="thumb"
                                                                thumbSize={64}
                                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <ChefHat className="w-6 h-6 text-brand-text-blue" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Meal Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium text-brand-text-blue uppercase tracking-wide mb-1">
                                                            {meal.mealType.replace('_', ' ')}
                                                        </div>
                                                        <h3 className="font-semibold text-foreground group-hover:text-brand-text-blue transition-colors truncate">
                                                            {meal.recipe.name}
                                                        </h3>
                                                        {(meal.recipe.prepTime ||
                                                            meal.recipe.cookTime) && (
                                                            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                                                <span className="flex items-center gap-1">
                                                                    <Clock className="w-3.5 h-3.5" />
                                                                    {(meal.recipe.prepTime || 0) +
                                                                        (meal.recipe.cookTime ||
                                                                            0)}{' '}
                                                                    min
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-brand-text-blue transition-colors" />
                                                </Link>
                                            ),
                                        )}
                                    </div>
                                ) : planUpcoming && plan ? (
                                    <div className="text-center py-6">
                                        <h3 className="font-semibold text-foreground mb-1">
                                            Nothing to eat from the plan yet.
                                        </h3>
                                        <p className="text-muted-foreground text-sm">
                                            Day 1 is {formatLongDate(plan.startDate)}. You can
                                            already browse the whole menu.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 bg-brand-fill-green rounded-full flex items-center justify-center mx-auto mb-4">
                                            <UtensilsCrossed className="w-8 h-8 text-brand-fill-ink" />
                                        </div>
                                        <h3 className="font-semibold text-foreground mb-2">
                                            No menu assigned yet
                                        </h3>
                                        <p className="text-muted-foreground text-sm mb-4">
                                            Browse our recipe collection while you wait for your
                                            program to start.
                                        </p>
                                        <Link to="/recipes">
                                            <Button>Browse Recipes</Button>
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 bg-brand-fill-green rounded-xl flex items-center justify-center">
                                        <Star className="w-5 h-5 text-brand-fill-ink" />
                                    </div>
                                </div>
                                <div className="text-2xl font-bold text-foreground">
                                    {totalWellnessPoints}
                                </div>
                                <div className="text-sm text-muted-foreground">Points Today</div>
                            </div>

                            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 bg-brand-fill-orange rounded-xl flex items-center justify-center">
                                        <UtensilsCrossed className="w-5 h-5 text-brand-fill-ink" />
                                    </div>
                                </div>
                                <div className="text-2xl font-bold text-foreground">
                                    {mealsLogged}
                                </div>
                                <div className="text-sm text-muted-foreground">Meals Logged</div>
                            </div>

                            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 bg-brand-fill-lime rounded-xl flex items-center justify-center">
                                        <BookOpen className="w-5 h-5 text-brand-fill-ink" />
                                    </div>
                                </div>
                                <div className="text-2xl font-bold text-foreground">
                                    {hasJournalEntry ? (
                                        <CheckCircle2 className="w-6 h-6 text-brand-text-green" />
                                    ) : (
                                        <Circle className="w-6 h-6 text-muted-foreground" />
                                    )}
                                </div>
                                <div className="text-sm text-muted-foreground">Journal Entry</div>
                            </div>

                            <div className="bg-card rounded-2xl p-5 shadow-sm border border-border">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 bg-brand-fill-yellow rounded-xl flex items-center justify-center">
                                        <CalendarDays className="w-5 h-5 text-brand-fill-ink" />
                                    </div>
                                </div>
                                <div className="text-2xl font-bold text-foreground">
                                    {assignment ? 'Active' : 'None'}
                                </div>
                                <div className="text-sm text-muted-foreground">Program</div>
                            </div>
                        </div>

                        {/* Featured Recipes */}
                        <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                                    <ChefHat className="w-5 h-5 text-brand-text-blue" />
                                    Explore Recipes
                                </h2>
                                <Link to="/recipes">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-brand-text-blue hover:bg-brand-header-wash"
                                    >
                                        View All
                                        <ArrowRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                            <p className="text-muted-foreground mb-4">
                                Discover healthy, delicious recipes crafted for your wellness
                                journey.
                            </p>
                            <Link to="/recipes">
                                <div className="relative h-40 rounded-xl overflow-hidden bg-brand-header group cursor-pointer">
                                    <div className="absolute inset-0 transition-colors group-hover:bg-black/10" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <ChefHat className="w-12 h-12 mx-auto mb-2 opacity-90" />
                                            <span className="font-semibold text-lg">
                                                Browse Recipe Library
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Sidebar - Right column */}
                    <div className="space-y-6">
                        {/* Daily Journal Card */}
                        {/* One ground in both states, rather than a green gradient
                            for "done" and a purple one for "not done". Done is
                            already said three ways — the two ticks, the numbers,
                            and the panel that replaces the button — so the card's
                            own colour was never carrying it alone. Keeping it fixed
                            means one ink, and every pairing in here is a flat
                            colour on a flat colour that can actually be measured.

                            The wells are bg-black/15 rather than the old
                            bg-white/20: darkening the blue takes white from 4.59:1
                            to 9.0:1, and the opacity-90/75/60 utilities that used
                            to sit on this text are gone — at those alphas the small
                            labels landed at 3.98:1 and below. Hierarchy is size and
                            weight here instead. */}
                        <div className="rounded-2xl bg-brand-header p-6 text-brand-header-ink">
                            <h3 className="text-lg font-bold flex items-center mb-4">
                                <BookOpen className="w-5 h-5 mr-2" />
                                Today's Journal
                            </h3>

                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="bg-black/15 rounded-xl p-3">
                                    <div className="text-2xl font-bold">{totalWellnessPoints}</div>
                                    <div className="text-xs">Total Points</div>
                                </div>
                                <div className="bg-black/15 rounded-xl p-3">
                                    <div className="text-2xl font-bold">{mealsLogged}</div>
                                    <div className="text-xs">Meals Logged</div>
                                </div>
                            </div>

                            {/* Progress Checklist */}
                            <div className="space-y-2 mb-4">
                                <div className="flex items-center gap-2 text-sm">
                                    {hasJournalEntry ? (
                                        <CheckCircle2 className="w-4 h-4" />
                                    ) : (
                                        <Circle className="w-4 h-4" />
                                    )}
                                    <span className={hasJournalEntry ? 'font-semibold' : ''}>
                                        Daily reflection
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    {mealsLogged > 0 ? (
                                        <CheckCircle2 className="w-4 h-4" />
                                    ) : (
                                        <Circle className="w-4 h-4" />
                                    )}
                                    <span className={mealsLogged > 0 ? 'font-semibold' : ''}>
                                        Log a meal
                                    </span>
                                </div>
                            </div>

                            {isJournalComplete && mealsLogged > 0 ? (
                                /* The one place the reward colour lands: a single
                                   self-contained panel on the green fill with the
                                   ink that fill takes (6.52:1), instead of
                                   re-inking the whole card. */
                                <div className="rounded-xl bg-brand-fill-green p-4 text-center text-brand-fill-ink">
                                    <div className="text-lg font-semibold mb-1">Great job!</div>
                                    <div className="text-sm">
                                        You've completed today's wellness tasks
                                    </div>
                                </div>
                            ) : (
                                <Link to="/journal">
                                    <button className="w-full bg-black/15 hover:bg-black/25 text-brand-header-ink py-3 px-4 rounded-xl transition-all duration-200 font-semibold">
                                        {!hasJournalEntry
                                            ? 'Start Journal Entry'
                                            : 'Continue Journaling'}
                                    </button>
                                </Link>
                            )}
                        </div>

                        {/* Daily Tip */}
                        {/* The warm tint is carried by a 4px bar of the yellow
                            fill rather than by tinting the card and its text.
                            That keeps the fill doing the one thing it is allowed
                            to do, and leaves the tip itself on the ordinary card
                            surface where the body copy already passes. */}
                        <div className="bg-card rounded-2xl p-6 border border-border border-l-4 border-l-brand-fill-yellow">
                            <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                                <span className="text-xl">💡</span>
                                Wellness Tip
                            </h3>
                            <p className="text-foreground text-sm leading-relaxed">
                                Start your day with a glass of water before breakfast. It helps
                                kickstart your metabolism and supports healthy digestion.
                            </p>
                        </div>

                        {/* Quick Links */}
                        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
                            <h3 className="font-bold text-foreground mb-4">Quick Links</h3>
                            <div className="space-y-2">
                                <Link
                                    to="/my-menu"
                                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-brand-header-wash transition-colors group"
                                >
                                    <div className="w-8 h-8 bg-brand-fill-green rounded-lg flex items-center justify-center">
                                        <CalendarDays className="w-4 h-4 text-brand-fill-ink" />
                                    </div>
                                    <span className="text-foreground group-hover:text-brand-text-blue transition-colors font-medium">
                                        My Weekly Menu
                                    </span>
                                </Link>
                                <Link
                                    to="/recipes"
                                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-brand-header-wash transition-colors group"
                                >
                                    <div className="w-8 h-8 bg-brand-fill-orange rounded-lg flex items-center justify-center">
                                        <UtensilsCrossed className="w-4 h-4 text-brand-fill-ink" />
                                    </div>
                                    <span className="text-foreground group-hover:text-brand-text-blue transition-colors font-medium">
                                        Browse Recipes
                                    </span>
                                </Link>
                                <Link
                                    to="/journal"
                                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-brand-header-wash transition-colors group"
                                >
                                    <div className="w-8 h-8 bg-brand-fill-lime rounded-lg flex items-center justify-center">
                                        <BookOpen className="w-4 h-4 text-brand-fill-ink" />
                                    </div>
                                    <span className="text-foreground group-hover:text-brand-text-blue transition-colors font-medium">
                                        Wellness Journal
                                    </span>
                                </Link>
                                <Link
                                    to="/progress-report"
                                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-brand-header-wash transition-colors group"
                                >
                                    <div className="w-8 h-8 bg-brand-fill-yellow rounded-lg flex items-center justify-center">
                                        <ClipboardList className="w-4 h-4 text-brand-fill-ink" />
                                    </div>
                                    <span className="text-foreground group-hover:text-brand-text-blue transition-colors font-medium">
                                        Weekly Progress Report
                                    </span>
                                </Link>
                                <Link
                                    to="/progress"
                                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-brand-header-wash transition-colors group"
                                >
                                    <div className="w-8 h-8 bg-brand-fill-green rounded-lg flex items-center justify-center">
                                        <LineChart className="w-4 h-4 text-brand-fill-ink" />
                                    </div>
                                    <span className="text-foreground group-hover:text-brand-text-blue transition-colors font-medium">
                                        My Progress
                                    </span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
